/**
 * Member ID Utilities - Unified member identification system
 *
 * This utility provides consistent member ID handling across the entire system.
 * It standardizes the conversion between different ID formats used in various contexts.
 */

import type { User } from '../types/index.d.ts';

export interface MemberIdFormats {
  /** Raw database ID (e.g., "1234567890") */
  raw: string;
  /** JWT token format (e.g., "member_1234567890") */
  token: string;
  /** API endpoint format (same as raw for consistency) */
  api: string;
}

/**
 * Extracts the raw member ID from any format
 */
export function extractRawMemberId(id: string | undefined | null): string | null {
  if (!id || typeof id !== 'string') return null;
  
  // If it's already a raw ID (no prefix), return as-is
  if (!id.includes('_')) return id;
  
  // Handle prefixed formats like "member_123" or "admin_as_member_123"
  if (id.startsWith('member_')) {
    return id.replace('member_', '');
  }
  
  if (id.includes('_as_member_')) {
    return id.split('_as_member_')[1];
  }
  
  // Fallback: return the part after the last underscore
  const parts = id.split('_');
  return parts[parts.length - 1];
}

/**
 * Converts raw member ID to JWT token format
 */
export function toTokenFormat(rawId: string): string {
  if (!rawId) throw new Error('Raw member ID is required');
  
  // If already in token format, return as-is
  if (rawId.startsWith('member_')) return rawId;
  
  return `member_${rawId}`;
}

/**
 * Converts any member ID format to API format (raw)
 */
export function toApiFormat(id: string | undefined | null): string | null {
  return extractRawMemberId(id);
}

/**
 * Gets all ID formats for a member
 */
export function getMemberIdFormats(id: string | undefined | null): MemberIdFormats | null {
  const rawId = extractRawMemberId(id);
  if (!rawId) return null;
  
  return {
    raw: rawId,
    token: toTokenFormat(rawId),
    api: rawId
  };
}

/**
 * Validates if two member IDs refer to the same member (regardless of format)
 */
export function isSameMember(id1: string | undefined | null, id2: string | undefined | null): boolean {
  const raw1 = extractRawMemberId(id1);
  const raw2 = extractRawMemberId(id2);
  
  return raw1 !== null && raw2 !== null && raw1 === raw2;
}

/**
 * Checks if a user can access a member's data based on role and ID matching
 */
export function canAccessMemberData(
  user: User | null | undefined,
  targetMemberId: string | undefined | null,
  allowedRoles: string[] = ['admin', 'finance_admin', 'town_admin', 'member_admin']
): boolean {
  if (!user || !targetMemberId) return false;
  
  // Admin roles can access any member data (with town restrictions handled elsewhere)
  if (allowedRoles.includes(user.role)) return true;
  
  // Members can only access their own data
  if (user.role === 'member' || user.role === 'member_portal_admin') {
    return isSameMember(user.memberId || user.id, targetMemberId);
  }
  
  return false;
}

/**
 * Gets the correct member ID to use for API calls from user context
 */
export function getMemberIdForApi(user: User | null | undefined): string | null {
  if (!user) return null;
  
  // For member users, extract from memberId or id
  if (user.role === 'member' || user.role === 'member_portal_admin') {
    // First try memberId, then id, handling the case where the database
    // might still contain prefixed IDs that haven't been migrated yet
    const candidateId = user.memberId || user.id;
    const rawId = extractRawMemberId(candidateId);
    
    // If the raw extraction gives us something different, return the raw version
    // Otherwise, return the original (for cases where DB still has prefixed IDs)
    return rawId !== candidateId ? rawId : candidateId;
  }
  
  return null;
}

/**
 * Normalizes member identifier for database queries
 * Handles username, raw ID, and prefixed ID formats
 */
export function normalizeMemberIdentifier(identifier: string | undefined | null): {
  type: 'id' | 'username' | null;
  value: string | null;
} {
  if (!identifier || typeof identifier !== 'string') {
    return { type: null, value: null };
  }
  
  // If it looks like a username (contains letters and is short)
  if (/^[A-Z]{3}\d{3}$/.test(identifier)) {
    return { type: 'username', value: identifier };
  }
  
  // If it's a numeric string or prefixed ID, treat as ID
  const rawId = extractRawMemberId(identifier);
  if (rawId && /^\d+$/.test(rawId)) {
    return { type: 'id', value: rawId };
  }
  
  // Default to username for non-numeric strings
  return { type: 'username', value: identifier };
}

/**
 * Debug utility to log member ID information
 */
export function debugMemberIds(c: any, context: string, ...ids: Array<{ label: string; value: string | undefined | null }>) {
  // Check if we're in development mode using Hono context
  const isDevelopment = c?.env?.NODE_ENV === 'development' || c?.env?.DEV === 'true';
  
  if (isDevelopment) {
    console.group(`🔍 Member ID Debug - ${context}`);
    ids.forEach(({ label, value }) => {
      const formats = getMemberIdFormats(value);
      console.log(`${label}:`, {
        original: value,
        formats: formats || 'Invalid ID'
      });
    });
    console.groupEnd();
  }
}